import React from 'react';
import Modal from '@hero/ui-kit/components/Modal';
import H from '@hero/ui-kit/typography/H';
import P from '@hero/ui-kit/typography/P';
import Button from '@hero/ui-kit/inputs/Button';
import Section from '@hero/ui-kit/layout/Section';
import useSendSms from '../../api/useSendSms';
import useSendEmail from '../../api/useSendEmail';
import { AddressValidationStatus, SmsType } from '../../types';
import * as Style from './style.module.scss';

// HWM-22633 SRS V4: canned SMS templates are Schedule appointment
// (sms_type=medical_check), Reschedule appointment (sms_type=reschedule), and
// Address (sms_type=address) — plus Free form. Body interpolation happens in
// notification-api. Email branch renders a static summary card; the Medical
// Check email body is the SES template hero-${PREFIX}-hero-rtm-medical-check.

type CommType =
    | 'sms_free_form'
    | 'sms_medical_check'
    | 'sms_reschedule'
    | 'sms_address'
    | 'email_medical_check';

interface LeadAddressForPreview {
    address_line_1?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
}

interface CommunicationModalProps {
    leadId: string;
    patientName?: string;
    patientFirstName?: string;
    providerName?: string;
    peName?: string;
    /** No longer read after round 7 — kept optional so callers compile unchanged. */
    addressValidationStatus?: AddressValidationStatus | null;
    /** No longer read after round 7 — kept optional so callers compile unchanged. */
    leadAddress?: LeadAddressForPreview;
    /** Lead's email address; rendered in the Medical Check email summary card. */
    leadEmail?: string | null;
    messagingEnabled?: boolean;
    onClose: () => void;
}

// SRS V4 preview copy (display-only; the real body is rendered server-side).
const buildSchedulePreview = (firstName: string, peName: string, providerName: string) =>
    `Hi ${firstName || '[First name]'}, this is ${peName || '[PE name]'} from ${providerName || '[Provider name]'}'s office. I'm reaching out with good news - your plan may support remote care programs, that include Hero smart dispenser. The last step is a short appointment with one of our clinicians to verify insurance and confirm medical necessity, and then Hero ships to you automatically. Book here: [link]. Reply with any questions!`;

const buildReschedulePreview = (firstName: string, peName: string, providerName: string) =>
    `Hi ${firstName || '[First name]'}, this is ${peName || '[PE name]'} from ${providerName || '[Provider name]'}'s office. You missed your recent appointment for our remote care program that includes Hero smart dispenser— no worries! You can reschedule here: [link]. Reply with any questions!`;

const buildAddressPreview = (firstName: string, peName: string, providerName: string) =>
    `Hi ${firstName || '[First name]'}, this is ${peName || '[PE name]'} from ${providerName || '[Provider name]'}'s office — great news, your Hero smart dispenser is approved and almost on its way! Before we ship, I just want to confirm we have the right address on file: [address]. Does that look correct? Reply YES to confirm or reply with your updated address and I'll get that sorted before we ship.`;

const CommunicationModal: React.FC<CommunicationModalProps> = ({
    leadId,
    patientName,
    patientFirstName,
    providerName,
    peName,
    leadEmail,
    messagingEnabled = true,
    onClose
}) => {
    const [commType, setCommType] = React.useState<CommType | ''>('');
    const [smsMessage, setSmsMessage] = React.useState('');

    const { mutateAsync: sendSms, isPending: isSendingSms } = useSendSms(onClose);
    const { mutateAsync: sendEmail, isPending: isSendingEmail } = useSendEmail(onClose);

    const firstName = patientFirstName || patientName?.split(' ')[0] || '';

    const cannedSmsPreview = React.useMemo(() => {
        const pe = peName || '';
        const prov = providerName || '';
        switch (commType) {
            case 'sms_medical_check':
                return buildSchedulePreview(firstName, pe, prov);
            case 'sms_reschedule':
                return buildReschedulePreview(firstName, pe, prov);
            case 'sms_address':
                return buildAddressPreview(firstName, pe, prov);
            default:
                return '';
        }
    }, [commType, firstName, peName, providerName]);

    const options: { value: CommType; label: string }[] = [
        { value: 'sms_free_form', label: 'SMS — Free form' },
        { value: 'sms_medical_check', label: 'SMS — Schedule appointment' },
        { value: 'sms_reschedule', label: 'SMS — Reschedule appointment' },
        { value: 'sms_address', label: 'SMS — Address' },
        { value: 'email_medical_check', label: 'Email — Medical check' }
    ];

    const handleSend = async () => {
        if (!commType) return;
        switch (commType) {
            case 'sms_free_form': {
                if (!smsMessage.trim()) return;
                await sendSms({
                    payload: {
                        lead_id: leadId,
                        sms_type: 'free_form',
                        message_body: smsMessage.trim()
                    }
                });
                return;
            }
            case 'sms_medical_check':
            case 'sms_reschedule':
            case 'sms_address': {
                const smsTypeByComm: Record<
                    'sms_medical_check' | 'sms_reschedule' | 'sms_address',
                    SmsType
                > = {
                    sms_medical_check: 'medical_check',
                    sms_reschedule: 'reschedule',
                    sms_address: 'address'
                };
                await sendSms({
                    payload: { lead_id: leadId, sms_type: smsTypeByComm[commType] }
                });
                return;
            }
            case 'email_medical_check': {
                await sendEmail({ payload: { lead_id: leadId } });
                return;
            }
        }
    };

    const isLoading = isSendingSms || isSendingEmail;
    // HWM-22633 round 7: gate the Email Send button on a non-empty
    // ``leadEmail`` — otherwise the backend would return
    // ``missing_receiver`` and the agent sees a guaranteed failure toast.
    // The summary card already explains "(no email on file)" so disabling
    // the button just makes the modal honest.
    const isCannedSms =
        commType === 'sms_medical_check' ||
        commType === 'sms_reschedule' ||
        commType === 'sms_address';
    const canSend =
        commType === 'sms_free_form'
            ? smsMessage.trim().length > 0
            : isCannedSms
              ? true
              : commType === 'email_medical_check'
                ? !!leadEmail
                : false;

    if (!messagingEnabled) {
        return (
            <Modal isCancelable onClose={onClose} className={Style.modal}>
                <div className={Style.modalBody}>
                    <H role="h4" centered noDefaultMargin>
                        Send communication
                    </H>
                    <P noDefaultMargin>
                        Messaging is not enabled for this provider. Contact ops to onboard the
                        provider to portal SMS/email.
                    </P>
                </div>
            </Modal>
        );
    }

    return (
        <Modal isCancelable onClose={onClose} className={Style.modal}>
            <div className={Style.modalBody}>
                <H role="h4" centered noDefaultMargin>
                    Send communication
                </H>

                <div className={Style.fieldGroup}>
                    <P noDefaultMargin className={Style.uppercaseLabel}>
                        Communication type
                    </P>
                    <select
                        name="comm_type"
                        value={commType}
                        onChange={(e) => setCommType(e.currentTarget.value as CommType)}
                        className={Style.selectInput}
                        data-placeholder={commType ? 'false' : 'true'}
                    >
                        <option value="" disabled>
                            Select from list
                        </option>
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                </div>

                {commType === 'sms_free_form' ? (
                    <div className={Style.fieldGroup}>
                        <P noDefaultMargin className={Style.uppercaseLabel}>
                            New SMS
                        </P>
                        <textarea
                            value={smsMessage}
                            onChange={(e) => setSmsMessage(e.target.value)}
                            maxLength={1600}
                            className={Style.textarea}
                            placeholder="Type your message…"
                        />
                        <P size="small" noDefaultMargin className={Style.charCount}>
                            {smsMessage.length}/1600
                        </P>
                    </div>
                ) : null}

                {isCannedSms ? (
                    <div className={Style.fieldGroup}>
                        <P noDefaultMargin className={Style.uppercaseLabel}>
                            Preview
                        </P>
                        <div className={Style.preview}>{cannedSmsPreview}</div>
                    </div>
                ) : null}

                {commType === 'email_medical_check' ? (
                    <div className={Style.fieldGroup}>
                        <P noDefaultMargin className={Style.uppercaseLabel}>
                            Medical Check email
                        </P>
                        <div className={Style.preview}>
                            <P noDefaultMargin>
                                <strong>To:</strong> {firstName ? `${firstName} ` : ''}
                                {leadEmail ? `<${leadEmail}>` : '(no email on file)'}
                            </P>
                            <P noDefaultMargin styles={{ marginTop: '0.6rem' }}>
                                Sends the canned SRS Medical Check outreach email. Subject, body,
                                and sender name are interpolated server-side from the lead and the
                                currently logged-in agent — no further input needed.
                            </P>
                        </div>
                    </div>
                ) : null}

                <Section noDefaultPadding className={Style.actionsCentered}>
                    <Button variant="outlineSecondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button onClick={handleSend} disabled={!canSend || isLoading}>
                        {isLoading ? 'Sending…' : 'Send'}
                    </Button>
                </Section>
            </div>
        </Modal>
    );
};

export default CommunicationModal;
